// ============================================================================
// client/entree-scene.ts — LES ENTRÉES EN SCÈNE, EN JEU
// ----------------------------------------------------------------------------
// Trois secondes au début d'un niveau : le héros arrive, puis la partie commence.
// Récompense de fin de campagne (catalogue : shared/entrees.ts).
//
// ⚠️ CE MODULE EST LE SEUL ENDROIT OÙ CES SIX ANIMATIONS EXISTENT. La planche
// /pages/entrees.html les IMPORTE d'ici ; elle n'en garde pas de copie. C'est la
// leçon de la vague du 30/07 : garder un double du trait dans la planche
// recrée le défaut même que la planche sert à éviter — deux versions qui
// divergent, dont une seule est validée.
//
// TROIS PROPRIÉTÉS, et la première est un contrat :
//
//  1. RIEN ICI NE TOUCHE À LA SIMULATION. Ce module ne fait que peindre. Le gel
//     de la partie est assuré ailleurs, par le mécanisme qui existait déjà :
//     `armed()` (client/solo.ts) tient le niveau figé — chrono à zéro, moines
//     immobiles — tant que le joueur n'a pas donné son premier ordre. L'entrée
//     s'y branche : tant qu'elle joue, rien n'avance. Pas une seconde offerte,
//     pas un record menacé.
//
//  2. TOUT EST EN PROPORTION DU SPAWN RÉEL. `cx` / `ySol` viennent du coureur,
//     pas d'une constante : sinon toutes les entrées joueraient à la colonne 13
//     quel que soit le niveau.
//
//  3. AUCUN HASARD. Ni Math.random ni Date.now : deux joueurs qui commencent le
//     même niveau voient la même chose, et une particule tirée au sort à chaque
//     image scintille au lieu de voler. Le désordre vient d'un hachage.
// ============================================================================

import { chapeauDuJeu, tenueDuJeu } from './baroudeur-papier'
import { chapeau4, melange, OMBRE_TON, pantin, POSES, rect, rgba, type Pose, type Tenue } from './papier'

type Ctx = CanvasRenderingContext2D

/** La durée d'une entrée. Le niveau reste gelé exactement ce temps-là. */
export const ENTREE_MS = 3000

/** Une case terrain à l'écran, et la taille du héros dans cette case. */
const CASE = 48, HERO = 44
const OR = '#ffd23f', OR_CLAIR = '#fff3c4'

/**
 * Ce que la scène a besoin de savoir du niveau. Tout est optionnel sauf le
 * point d'arrivée : une entrée doit pouvoir jouer sur n'importe quelle carte,
 * y compris une carte de la famille dont on ne sait rien.
 */
export interface SceneEntree {
  /** Le centre du coureur au spawn, en pixels écran. */
  cx: number
  /** Le sol sous ses pieds (le haut de la case pleine), en pixels écran. */
  ySol: number
  /** L'aspect du héros : ses cosmétiques équipés. */
  hue?: number
  outfit?: string
  hat?: string
  /** Vrai si la case du sol à cette abscisse écran est pleine. Sert au wagonnet,
   *  qui ne doit pas poser ses rails au-dessus d'un trou. */
  solide?: (x: number) => boolean
}

/* ═══════════════════════════════════════════════════════════ LES OUTILS DU TEMPS */

/** La part écoulée d'une phase, bornée à [0,1]. Tout le minutage passe par là :
 *  une phase se lit alors comme une ligne de scénario. */
const ph = (ms: number, a: number, b: number): number => Math.max(0, Math.min(1, (ms - a) / (b - a)))
/** Départ franc, arrivée qui s'adoucit : une chute, un freinage. */
const sortie = (t: number): number => 1 - (1 - t) * (1 - t)
/** Départ mou, arrivée lancée : une accélération, une remontée. */
const entree = (t: number): number => t * t
/** Les deux à la fois : un mouvement mécanique, un battant qui s'ouvre. */
const doux = (t: number): number => t * t * (3 - 2 * t)
/** Un rebond court à l'arrivée : ce qui distingue une pièce POSÉE d'une pièce
 *  qui s'arrête net. */
const rebond = (t: number): number => t < 1 ? 1 - Math.pow(2, -9 * t) * Math.abs(Math.cos(t * 9)) : 1
/** Le désordre déterministe : deux entiers entrent, un nombre entre 0 et 1 sort. */
const alea = (i: number, sel: number): number => {
  let h = ((i + 1) * 73856093) ^ ((sel + 1) * 19349663)
  h = (h ^ (h >>> 13)) >>> 0
  return (h % 1000) / 1000
}

/* ═══════════════════════════════════════════════════════════ LES EFFETS COMMUNS */

/** L'ombre portée du héros. Elle grossit et pâlit avec la hauteur : c'est elle,
 *  et elle seule, qui dit au joueur qu'un corps tombe et où il va tomber. */
function ombreAuSol(g: Ctx, x: number, ySol: number, hauteur: number): void {
  const k = Math.max(0, Math.min(1, hauteur / (CASE * 8)))
  g.fillStyle = rgba('#000000', .34 * (1 - k * .75))
  g.beginPath(); g.ellipse(x, ySol - 2, 15 + k * 22, 5 + k * 4, 0, 0, 7); g.fill()
}

/** La poussière d'un impact : des bouffées qui partent à l'horizontale, montent
 *  un peu, puis retombent en s'étalant. */
function poussiere(g: Ctx, x: number, y: number, age: number, force: number, sel: number): void {
  if (age <= 0 || age > 1) return
  for (let i = 0; i < 14; i++) {
    const cote = i % 2 ? 1 : -1
    const a = alea(i, sel)
    const dx = cote * (40 + a * 90) * force * sortie(age)
    const dy = -(10 + a * 26) * force * Math.sin(age * Math.PI * .8)
    const r = (5 + a * 9) * force * (0.5 + age * 1.3)
    g.fillStyle = rgba('#c9b48f', .42 * (1 - age) * (1 - age))
    g.beginPath(); g.arc(x + dx, y + dy, r, 0, 7); g.fill()
  }
}

/** Des gravats : des morceaux de papier qui partent et retombent avec la
 *  gravité, en tournant. Ce sont eux qui donnent le POIDS ; la poussière seule
 *  fait mou. */
function gravats(g: Ctx, x: number, y: number, age: number, n: number, force: number, sel: number, col = '#8a6a4a'): void {
  if (age <= 0 || age > 1) return
  for (let i = 0; i < n; i++) {
    const a = alea(i, sel), b = alea(i + 40, sel)
    const T = age * 1.2
    const px = x + (a - .5) * 700 * force * T
    const py = y - (180 + b * 340) * force * T + 900 * T * T
    if (py > y + 8) continue                        // il a touché le sol : on l'oublie
    const s = (4 + b * 6) * force
    g.save(); g.translate(px, py); g.rotate(T * (6 + a * 10) * (a > .5 ? 1 : -1))
    g.fillStyle = rgba(OMBRE_TON, .3); g.fillRect(-s / 2 + 2, -s / 2 + 2, s, s)
    g.fillStyle = col; g.fillRect(-s / 2, -s / 2, s, s)
    g.restore()
  }
}

/** Des étincelles : chaudes, additives, et surtout COURTES. Une étincelle qui
 *  vit une seconde se lit comme une luciole. */
function etincelles(g: Ctx, x: number, y: number, age: number, n: number, sens: number, sel: number): void {
  if (age <= 0 || age > 1) return
  g.save(); g.globalCompositeOperation = 'lighter'
  for (let i = 0; i < n; i++) {
    const a = alea(i, sel), b = alea(i + 70, sel)
    const vie = .35 + a * .5
    const T = (age * 1.6 - a * .3) / vie
    if (T < 0 || T > 1) continue
    const px = x + sens * (200 + a * 620) * T * vie
    const py = y - (60 + b * 300) * T * vie + 1400 * (T * vie) * (T * vie)
    g.fillStyle = rgba(T < .5 ? '#fff6d0' : '#ffa63a', .9 * (1 - T))
    g.beginPath(); g.arc(px, py, (1.6 + b * 2.2) * (1 - T), 0, 7); g.fill()
  }
  g.restore()
}

/** L'onde de choc : deux anneaux décalés qui s'ouvrent et s'effacent. Un seul,
 *  épais, se lirait comme un rond posé au sol. */
function onde(g: Ctx, x: number, y: number, age: number, force: number): void {
  if (age <= 0 || age > 1) return
  for (const [d, w] of [[0, 4], [.14, 2.5]] as const) {
    const t = age - d
    if (t <= 0) continue
    const R = sortie(t) * 210 * force
    g.strokeStyle = rgba('#f0e2c4', .5 * (1 - t) * (1 - t))
    g.lineWidth = w * (1 - t * .6)
    g.beginPath(); g.ellipse(x, y, R, R * .32, 0, 0, 7); g.stroke()
  }
}

/** Le tremblement. Court, sinon on lit un défaut d'affichage plutôt qu'un choc. */
function secousse(g: Ctx, age: number, force: number): void {
  if (age <= 0 || age > 1) return
  const A = force * 9 * (1 - age) * (1 - age)
  g.translate(Math.sin(age * 61) * A, Math.cos(age * 47) * A * .6)
}

/** Un voile sombre par-dessus le niveau : le regard doit avoir un endroit où se
 *  poser AVANT que le héros n'arrive, sinon l'œil part explorer la carte et rate
 *  l'entrée. Il se lève toujours avant la fin. */
function voile(g: Ctx, W: number, H: number, a: number): void {
  if (a <= 0) return
  g.fillStyle = rgba('#0b0814', a)
  g.fillRect(0, 0, W, H)
}

/** Le héros, avec SES cosmétiques : la tenue et l'accessoire équipés passent par
 *  les mêmes fonctions que le rendu du jeu. Une entrée en scène où le personnage
 *  ne porterait pas ses affaires se lirait comme un autre joueur. */
function heros(g: Ctx, s: SceneEntree, cx: number, base: number, pose: Pose | string, tick: number): void {
  const { base: chap, ajout } = chapeauDuJeu(s.hat ?? '')
  const tenue = tenueDuJeu(s.hue ?? 0, s.outfit ?? '')
  pantin(g, cx, base, HERO, tenue, chap, pose, { tick })
  if (!ajout) return
  /* l'accessoire ADDITIF (auréole, fleurs) se pose au-dessus du crâne, dans le
     même repère que la tête — le geste exact de baroudeur-papier. */
  const p = typeof pose === 'string' ? (POSES[pose] ?? POSES.idle!) : pose
  const u = HERO / 44, TE = (p.tete ?? [0, 0, 0]) as [number, number, number]
  g.save()
  g.translate(cx + TE[0] * u * .5, base + TE[1] * u); g.rotate(TE[2])
  chapeau4Ajout(g, u, ajout, tenue)
  g.restore()
}
function chapeau4Ajout(g: Ctx, u: number, kind: string, tenue: Tenue): void {
  chapeau4(g, 1 * u, -47 * u, u, kind, tenue)
}

/* ═══════════════════════════════════════════════════════════════ LES SIX ENTRÉES */

/* ─── 1. LA CAGE DE MINE ─────────────────────────────────────────────────────
   0,00 s  le câble se tend, la cage entre par le haut
   1,10 s  contact, rebond, la poussière part
   1,45 s  la grille se relève
   1,95 s  il descend la marche
   2,45 s  la cage remonte et sort du cadre                                    */
function entreeCage(g: Ctx, W: number, H: number, ms: number, s: SceneEntree): void {
  const cx = s.cx, ySol = s.ySol
  const bw = CASE * 1.7, bh = CASE * 2.3
  /* ⚠️ La cage se POSE sur le sol, elle ne s'arrête pas « vers » le sol : le
     repère se calcule depuis sa hauteur. Réglé à l'œil, il flottait de quatorze
     pixels — invisible de loin, flagrant de près. */
  const descente = rebond(ph(ms, 0, 1250))
  const yCage = -CASE * 3.4 + (ySol - bh + CASE * 3.4) * descente
  const remonte = entree(ph(ms, 2450, 3000))
  const yFinal = yCage - remonte * (yCage + CASE * 4)
  const impact = ph(ms, 1100, 1750)
  const grille = doux(ph(ms, 1450, 1950))

  voile(g, W, H, .42 * (1 - ph(ms, 1500, 2400)))
  g.save()
  secousse(g, impact, 1)

  /* le câble VIBRE encore un instant après le contact : c'est ce qui dit qu'il
     porte une masse */
  const vib = Math.sin(ms / 26) * 3 * (1 - ph(ms, 1100, 1600))
  g.strokeStyle = '#6b5a3e'; g.lineWidth = 3
  g.beginPath(); g.moveTo(cx + vib, 0); g.lineTo(cx, yFinal + 6); g.stroke()

  g.save(); g.translate(cx, yFinal)
  g.fillStyle = rgba(OMBRE_TON, .32); g.fillRect(-bw / 2 + 5, 6, bw, bh)
  rect(g, -bw / 2, 0, bw, 8, '#7a4a2a', 'briques', false)
  rect(g, -bw / 2, bh - 12, bw, 12, '#7a4a2a', 'briques', false)
  for (const c of [-1, 1]) rect(g, c * (bw / 2 - 7), 6, 7, bh - 16, '#5a3620', 'plis', false)
  /* les barreaux du fond : ils doivent se lire DERRIÈRE le héros */
  g.strokeStyle = rgba('#3a2c1e', .55); g.lineWidth = 2
  for (let k = 1; k < 6; k++) { g.beginPath(); g.moveTo(-bw / 2 + k * (bw / 6), 8); g.lineTo(-bw / 2 + k * (bw / 6), bh - 12); g.stroke() }
  /* la grille avant, celle qui se relève */
  g.save(); g.translate(0, -grille * (bh - 16))
  g.strokeStyle = '#8a5a30'; g.lineWidth = 3
  for (let k = 0; k <= 4; k++) { g.beginPath(); g.moveTo(-bw / 2 + 6 + k * (bw - 12) / 4, 10); g.lineTo(-bw / 2 + 6 + k * (bw - 12) / 4, bh - 14); g.stroke() }
  for (let k = 0; k <= 2; k++) { g.beginPath(); g.moveTo(-bw / 2 + 6, 10 + k * (bh - 24) / 2); g.lineTo(bw / 2 - 6, 10 + k * (bh - 24) / 2); g.stroke() }
  g.restore()
  /* LA LANTERNE : il lui faut un BOÎTIER. Un disque jaune et un halo, à cette
     taille, ne se lisent pas comme une lampe mais comme une tache. */
  const bal = Math.sin(ms / 190) * .25 * (1 - ph(ms, 1100, 2200))
  g.save(); g.translate(bw / 2 - 13, 8); g.rotate(bal)
  g.strokeStyle = '#4a3320'; g.lineWidth = 2
  g.beginPath(); g.moveTo(0, 0); g.lineTo(0, 9); g.stroke()
  g.strokeStyle = '#6b5a3e'; g.lineWidth = 1.6
  g.beginPath(); g.arc(0, 12, 4, Math.PI, 0); g.stroke()
  g.fillStyle = '#4a3320'
  g.beginPath(); g.moveTo(-7, 15); g.lineTo(7, 15); g.lineTo(5, 11); g.lineTo(-5, 11); g.closePath(); g.fill()
  g.fillStyle = '#3a2c1e'; g.fillRect(-6, 28, 12, 3.5)
  g.fillStyle = rgba(OR_CLAIR, .92); g.fillRect(-4.5, 15, 9, 13)
  g.fillStyle = '#e8a020'
  g.beginPath(); g.ellipse(0, 22, 2.2, 4 + Math.sin(ms / 90) * .6, 0, 0, 7); g.fill()
  g.strokeStyle = '#3a2c1e'; g.lineWidth = 1.2
  g.beginPath(); g.moveTo(-4.5, 21.5); g.lineTo(4.5, 21.5); g.stroke()
  g.save(); g.globalCompositeOperation = 'lighter'
  const halo = g.createRadialGradient(0, 22, 2, 0, 22, 34)
  halo.addColorStop(0, rgba(OR, .3)); halo.addColorStop(1, rgba(OR, 0))
  g.fillStyle = halo; g.beginPath(); g.arc(0, 22, 34, 0, 7); g.fill(); g.restore()
  g.restore()
  g.restore()

  const dehors = ph(ms, 1950, 2450)
  if (dehors <= 0) {
    heros(g, s, cx, yFinal + bh - 12, POSES.idle!, ms / 16)
  } else {
    const x = cx + doux(dehors) * CASE * .9
    const y = yCage + bh - 12 + doux(dehors) * (ySol - (yCage + bh - 12))
    heros(g, s, x, y, dehors < .8 ? POSES.run1! : POSES.idle!, ms / 16)
  }

  poussiere(g, cx, ySol, impact, 1, 11)
  gravats(g, cx, ySol, ph(ms, 1100, 1900), 8, .7, 12)
  g.restore()
}

/* ─── 2. LE PERCEMENT ────────────────────────────────────────────────────────
   0,00 s  le sol tressaille, des cailloux sautent
   0,55 s  trois fissures s'ouvrent en éventail
   0,95 s  la brique éclate, les gravats partent
   1,25 s  la lampe sort du trou, puis lui
   2,45 s  il s'époussette, le trou se referme                                 */
function entreePercement(g: Ctx, W: number, H: number, ms: number, s: SceneEntree): void {
  const cx = s.cx, ySol = s.ySol
  const tremble = ph(ms, 0, 950)
  const fissure = ph(ms, 550, 950)
  const eclat = ph(ms, 950, 1700)
  const monte = ph(ms, 1250, 1950)
  const referme = ph(ms, 2450, 3000)

  voile(g, W, H, .38 * (1 - ph(ms, 900, 1800)))
  g.save()
  /* le tressaillement est CROISSANT : il annonce. Une secousse qui décroît
     dirait « c'est fini » avant que ce soit arrivé. */
  if (ms < 950) {
    const A = tremble * tremble * 4
    g.translate(Math.sin(ms / 21) * A, Math.cos(ms / 17) * A * .5)
  } else {
    secousse(g, ph(ms, 950, 1500), 1.3)
  }

  if (ms < 1000) {
    for (let i = 0; i < 7; i++) {
      const a = alea(i, 3)
      const t = ((ms / 400 + a) % 1)
      g.fillStyle = rgba('#6a5240', .8)
      g.fillRect(cx + (a - .5) * 150, ySol - Math.sin(t * Math.PI) * (6 + a * 14) * tremble - 3, 3 + a * 2, 3 + a * 2)
    }
  }

  if (eclat > 0) {
    const ouvre = Math.min(1, doux(eclat * 2)) * (1 - doux(referme))
    if (ouvre > .01) {
      g.save()
      g.beginPath()
      const w = CASE * ouvre
      g.moveTo(cx - w / 2, ySol)
      for (let k = 0; k <= 6; k++) g.lineTo(cx - w / 2 + (k / 6) * w, ySol + (k % 2 ? 5 : -3) * ouvre)
      g.lineTo(cx + w / 2, ySol + CASE); g.lineTo(cx - w / 2, ySol + CASE)
      g.closePath()
      g.fillStyle = '#140f1e'; g.fill()
      /* La lampe frontale monte du trou AVANT lui : on voit la lumière arriver
         avant de voir qui arrive. ⚠️ Elle est CLIPPÉE PAR LE TROU et monte en
         cône — un simple disque additif débordait et se lisait comme un ballon
         beige posé sur le sol. */
      g.clip()
      const src = ySol + CASE * (1 - monte) + 10
      const fais = g.createLinearGradient(cx, src, cx, src - CASE * 1.4)
      fais.addColorStop(0, rgba(OR_CLAIR, .5 * (1 - monte * .5)))
      fais.addColorStop(1, rgba(OR, 0))
      g.fillStyle = fais
      g.beginPath()
      g.moveTo(cx - 5, src); g.lineTo(cx - 26, src - CASE * 1.4)
      g.lineTo(cx + 26, src - CASE * 1.4); g.lineTo(cx + 5, src)
      g.closePath(); g.fill()
      g.restore()
    }
  }

  if (fissure > 0 && eclat < .5) {
    g.strokeStyle = rgba('#2a2018', .75 * (1 - eclat * 2)); g.lineWidth = 2
    for (let k = 0; k < 3; k++) {
      const a = -Math.PI / 2 + (k - 1) * .7
      const L = fissure * 34
      g.beginPath(); g.moveTo(cx, ySol - 2)
      g.lineTo(cx + Math.cos(a) * L * .5 + (k - 1) * 4, ySol - 2 + Math.sin(a) * L * .5)
      g.lineTo(cx + Math.cos(a) * L, ySol - 2 + Math.sin(a) * L)
      g.stroke()
    }
  }

  if (monte > 0) {
    const y = ySol + CASE * .9 * (1 - doux(monte))
    g.save()
    /* tant qu'il monte, on le clippe au-dessus du sol : sinon ses jambes
       flottent dans la roche */
    if (monte < 1) { g.beginPath(); g.rect(0, 0, W, ySol + 2); g.clip() }
    heros(g, s, cx, y, monte < .55 ? POSES.dig! : monte < .95 ? POSES.pickUp! : POSES.idle!, ms / 16)
    g.restore()
    if (ms > 2350) poussiere(g, cx + 8, ySol - 26, ph(ms, 2350, 2900), .35, 21)
  }

  gravats(g, cx, ySol, eclat, 22, 1.25, 22)
  poussiere(g, cx, ySol, ph(ms, 950, 1750), 1.15, 23)
  onde(g, cx, ySol, ph(ms, 950, 1500), .8)
  /* le flash de la casse : six centièmes de seconde, la seule image franche */
  const flash = ph(ms, 950, 1010)
  if (flash > 0 && flash < 1) {
    g.save(); g.globalCompositeOperation = 'lighter'
    g.fillStyle = rgba('#fff0c8', .45 * (1 - flash))
    g.beginPath(); g.arc(cx, ySol - 4, 20 + 46 * flash, 0, 7); g.fill(); g.restore()
  }
  g.restore()
}

/* ─── 3. LA RÉCEPTION ────────────────────────────────────────────────────────
   0,00 s  l'ombre apparaît au sol, minuscule
   0,35 s  il entre par le haut, en boule
   1,05 s  impact : onde, poussière, fissures
   1,80 s  il se relève lentement                                              */
const POSE_TROIS_POINTS: Pose = {
  nom: 'trois points', bassinY: 11, bassinRot: -.06, buste: [3, 11, .34], tete: [4, 10, -.2], ecrase: .93, solW: 1,
  cuAV: -.95, gnAV: 1.85, cuAR: .75, gnAR: 1.5, epAV: -.35, cdAV: -1.5, epAR: .5, cdAR: -.4, foulard: .15,
}
function entreeReception(g: Ctx, W: number, H: number, ms: number, s: SceneEntree): void {
  const cx = s.cx, ySol = s.ySol
  const chute = ph(ms, 350, 1050)
  const y = -CASE * 2 + (ySol + CASE * 2) * entree(chute)      // une chute ACCÉLÈRE
  const impact = ph(ms, 1050, 1800)
  const releve = ph(ms, 1800, 2500)

  voile(g, W, H, .45 * (1 - ph(ms, 1050, 2000)))
  g.save()
  secousse(g, ph(ms, 1050, 1450), 1.8)

  /* L'OMBRE EST LÀ AVANT LUI : le joueur sait où ça va tomber une demi-seconde
     avant que ça tombe. C'est elle qui transforme une chute en menace. */
  ombreAuSol(g, cx, ySol, ms < 1100 ? ySol - y : 0)

  /* Les fissures d'impact : COURTES et brisées en deux segments d'angles
     différents. Longues, elles traversaient l'image en traits bruns qui
     restaient après coup — on lisait un défaut d'affichage. */
  if (impact > 0) {
    g.strokeStyle = rgba('#2a2018', .42 * Math.min(1, impact * 5) * (1 - impact * .55))
    g.lineWidth = 1.4
    for (let k = 0; k < 7; k++) {
      const a = (k / 7) * Math.PI * 2 + .4
      const L = 12 + alea(k, 5) * 18
      const coude = a + (alea(k, 6) - .5) * .9
      const mx = cx + Math.cos(a) * L * .55, my = ySol - 1 + Math.sin(a) * L * .16
      g.beginPath(); g.moveTo(cx, ySol - 1); g.lineTo(mx, my)
      g.lineTo(mx + Math.cos(coude) * L * .5, my + Math.sin(coude) * L * .16)
      g.stroke()
    }
  }

  if (chute < 1) {
    const p = melange(POSES.fall!, POSES.fallA!, Math.sin(ms / 90) * .5 + .5)
    heros(g, s, cx, y, { ...p, etire: 1 + chute * .1 }, ms / 16)
    /* la traînée d'air : sans elle la chute paraît lente quoi qu'on fasse */
    if (chute > .4) {
      g.strokeStyle = rgba('#ffffff', .16 * chute); g.lineWidth = 2
      for (const dx of [-9, 0, 9]) { g.beginPath(); g.moveTo(cx + dx, y - 60 - chute * 90); g.lineTo(cx + dx, y - 44); g.stroke() }
    }
  } else if (releve <= 0) {
    /* l'écrasement dure un dixième de seconde ; sans lui la réception est molle */
    heros(g, s, cx, ySol, { ...POSE_TROIS_POINTS, ecrase: 1 - .18 * (1 - Math.min(1, ph(ms, 1050, 1200))) }, ms / 16)
  } else {
    heros(g, s, cx, ySol, melange(POSE_TROIS_POINTS, POSES.idle!, doux(releve)), ms / 16)
  }

  onde(g, cx, ySol, impact, 1.4)
  poussiere(g, cx, ySol, impact, 1.5, 31)
  gravats(g, cx, ySol, ph(ms, 1050, 1900), 14, 1, 32)
  g.restore()
}

/* ─── 4. LE WAGONNET ─────────────────────────────────────────────────────────
   0,00 s  il entre à pleine vitesse par la droite
   0,85 s  freinage, étincelles sous les roues
   1,45 s  il bondit, le wagonnet continue
   2,35 s  le wagonnet sort par la gauche                                      */
function dessineBenne(g: Ctx, bw: number, bh: number): void {
  g.fillStyle = rgba(OMBRE_TON, .3); g.fillRect(-bw / 2 + 4, -bh + 5, bw, bh)
  rect(g, -bw / 2, -bh, bw, bh, '#7a4a2a', 'briques', false)
  g.fillStyle = '#5a3620'; g.fillRect(-bw / 2, -bh, bw, 5)
  g.strokeStyle = '#8a8a94'; g.lineWidth = 3
  for (const dx of [-bw / 3, bw / 3]) { g.beginPath(); g.moveTo(dx, -bh + 4); g.lineTo(dx, 0); g.stroke() }
  g.strokeStyle = '#6a6a72'; g.lineWidth = 2
  g.beginPath(); g.moveTo(-bw / 2, -bh * .45); g.lineTo(bw / 2, -bh * .45); g.stroke()
}
function entreeWagonnet(g: Ctx, W: number, H: number, ms: number, s: SceneEntree): void {
  const cx = s.cx, ySol = s.ySol
  const arrive = sortie(ph(ms, 0, 1450))
  const xWag = W + CASE * 2 - (W + CASE * 2 - cx + CASE * 1.6) * arrive
  const fuite = entree(ph(ms, 1500, 3000))
  const xFinal = xWag - fuite * (xWag + CASE * 3)
  const saut = ph(ms, 1450, 1850)
  const freine = ph(ms, 850, 1500)
  const bw = CASE * 1.5, bh = CASE * .82

  voile(g, W, H, .34 * (1 - ph(ms, 900, 1900)))
  g.save()
  secousse(g, ph(ms, 1780, 2050), .7)

  /* LES RAILS, ET LE SEUL ENDROIT OÙ CETTE ENTRÉE PEUT MENTIR. Sur la planche
     je les ai posés sur toute la largeur ; en jeu, tous les niveaux n'ont pas un
     sol continu à hauteur du spawn. On ne pose donc un rail QUE sur une case
     réellement pleine : au-dessus d'un trou, la voie s'interrompt — ce qui est
     la vérité du terrain, et se lit très bien. */
  const plein = s.solide ?? ((): boolean => true)
  g.strokeStyle = rgba('#4a3a2a', .8); g.lineWidth = 3
  for (let x = 0; x < W; x += CASE) if (plein(x + CASE / 2)) { g.beginPath(); g.moveTo(x + 10, ySol - 5); g.lineTo(x + 10, ySol - 1); g.stroke() }
  g.strokeStyle = '#6a6a72'; g.lineWidth = 2.5
  for (const dy of [-7, -3]) for (let x = 0; x < W; x += CASE) {
    if (!plein(x + CASE / 2)) continue
    g.beginPath(); g.moveTo(x, ySol + dy); g.lineTo(x + CASE, ySol + dy); g.stroke()
  }

  const R = 13
  g.save(); g.translate(xFinal, ySol - 10)
  const vitesse = ms < 1450 ? (1 - sortie(ph(ms, 850, 1450))) : 1
  /* le flou de vitesse : deux images fantômes derrière, tant qu'il file */
  if (ms < 900) { g.globalAlpha = .35; for (const d of [22, 44]) { g.save(); g.translate(d, 0); dessineBenne(g, bw, bh); g.restore() } g.globalAlpha = 1 }
  dessineBenne(g, bw, bh)
  /* les roues tournent à la VITESSE RÉELLE (l'angle vient de la distance
     parcourue, pas du temps) : une roue qui tourne encore à l'arrêt est le
     défaut classique de ce genre d'animation */
  for (const dx of [-bw / 3, bw / 3]) {
    g.save(); g.translate(dx, 4); g.rotate((xFinal / R) + vitesse)
    g.fillStyle = '#4a4a52'; g.beginPath(); g.arc(0, 0, R, 0, 7); g.fill()
    g.strokeStyle = '#8a8a94'; g.lineWidth = 2
    for (let k = 0; k < 4; k++) { g.beginPath(); g.moveTo(0, 0); g.rotate(Math.PI / 2); g.lineTo(R - 2, 0); g.stroke() }
    g.restore()
  }
  g.restore()

  if (freine > 0 && freine < 1) {
    for (const dx of [-bw / 3, bw / 3]) etincelles(g, xFinal + dx, ySol - 4, freine, 10, -1, 41 + (dx > 0 ? 1 : 0))
  }

  if (saut <= 0) {
    heros(g, s, xFinal + 4, ySol - 10 - bh * .35, POSES.crouch!, ms / 16)
  } else if (saut < 1) {
    /* l'arc du saut est une PARABOLE, pas une interpolation : un bond droit se
       lit comme un ascenseur */
    const x = (xWag + 4) + (cx - xWag - 4) * saut
    const y = ySol - Math.sin(saut * Math.PI) * CASE * 1.5 - (1 - saut) * (bh * .35 + 10)
    ombreAuSol(g, x, ySol, ySol - y)
    heros(g, s, x, y, saut < .45 ? POSES.tramp1! : saut < .9 ? POSES.fall! : POSES.crouch!, ms / 16)
  } else {
    const p = ph(ms, 1850, 2400)
    heros(g, s, cx, ySol, p < 1 ? melange(POSES.crouch!, POSES.idle!, doux(p)) : POSES.idle!, ms / 16)
  }
  poussiere(g, cx, ySol, ph(ms, 1850, 2450), .8, 42)
  g.restore()
}

/* ─── 5. LE RIDEAU DE PAPIER ─────────────────────────────────────────────────
   0,00 s  l'écran est fermé, deux feuilles bord à bord
   0,45 s  elles se fendent, le bord frissonne
   1,90 s  elles sortent, les chutes de découpe retombent
   2,20 s  il se déplie et se lève                                             */
function entreeRideau(g: Ctx, W: number, H: number, ms: number, s: SceneEntree): void {
  const ouvre = doux(ph(ms, 450, 1900))
  const leve = ph(ms, 2150, 2800)
  heros(g, s, s.cx, s.ySol, leve <= 0 ? POSES.crouch! : melange(POSES.crouch!, POSES.idle!, doux(leve)), ms / 16)

  /* LES DEUX FEUILLES, du même papier que tout le reste. Le bord n'est pas
     droit : il sort des ciseaux, et il FRISSONNE au moment de la fente — c'est
     ce frisson, et rien d'autre, qui fait lire du papier et pas deux volets. */
  const larg = W / 2 + 30
  for (const cote of [-1, 1] as const) {
    const dx = cote * ouvre * (W / 2 + 40)
    const gliss = cote * ouvre * 8
    g.save()
    g.translate(dx, 0); g.rotate(cote * ouvre * .012)
    g.fillStyle = rgba('#000000', .38 * (1 - ouvre * .5))
    g.fillRect(cote < 0 ? -larg + 14 : W / 2 + 14, 10, larg, H)
    const x0 = cote < 0 ? -larg : W / 2
    rect(g, x0, -20, larg, H + 40, '#e8d8b8', 'quadrille', false)
    const bx = cote < 0 ? x0 + larg : x0
    const frisson = (1 - ph(ms, 450, 900)) * 6
    g.fillStyle = '#d8c49c'
    for (let y = -20; y < H + 20; y += 9) {
      const d = 3 + alea(y, 7) * 9 + Math.sin(y / 24 + ms / 120) * frisson
      g.fillRect(cote < 0 ? bx - d : bx, y + gliss, d, 9)
    }
    g.restore()
  }

  /* les chutes de découpe, qui retombent en tournant */
  const conf = ph(ms, 1500, 3000)
  if (conf > 0) {
    for (let i = 0; i < 26; i++) {
      const a = alea(i, 9), b = alea(i + 30, 9)
      const t = conf * (0.7 + a * .6) - a * .25
      if (t <= 0 || t > 1) continue
      const sz = 7 + b * 9
      g.save()
      g.translate((a * W) + Math.sin(t * 5 + i) * 40, -30 + t * (H + 60))
      g.rotate(t * 7 * (a > .5 ? 1 : -1) + i)
      g.fillStyle = rgba(OMBRE_TON, .22); g.fillRect(-sz / 2 + 2, -sz / 2 + 2, sz, sz * .7)
      g.fillStyle = ['#e8d8b8', '#d8a0a0', '#a0c8d8', OR][i % 4]!
      g.fillRect(-sz / 2, -sz / 2, sz, sz * .7)
      g.restore()
    }
  }
}

/* ─── 6. LA LAMPE ────────────────────────────────────────────────────────────
   L'entrée par DÉFAUT de toute campagne (shared/entrees.ts).
   0,00 s  noir, une silhouette à peine devinée
   0,45 s  l'allumette craque, le visage par en dessous
   1,05 s  la lampe prend, éclat blanc
   1,50 s  le halo s'étend et découvre le niveau                               */
function entreeLampe(g: Ctx, W: number, H: number, ms: number, s: SceneEntree): void {
  const cx = s.cx, ySol = s.ySol
  const tete = ySol - 34
  const allumette = ph(ms, 450, 1100)
  const lampe = ph(ms, 1050, 1500)
  const revele = doux(ph(ms, 1500, 2600))

  /* LA RÉVÉLATION : un noir percé d'un seul trou qui s'agrandit. Le trou est un
     chemin à règle PAIR-IMPAIR (un rectangle moins un disque) — une seule
     opération, sans calque ni masque. */
  const R = 30 + revele * (Math.hypot(W, H) * .62)
  g.save()
  g.beginPath()
  g.rect(0, 0, W, H)
  g.arc(cx, tete, R, 0, Math.PI * 2, true)
  g.fillStyle = rgba('#0b0814', .96 - revele * .55)
  g.fill('evenodd')
  const grd = g.createRadialGradient(cx, tete, R * .72, cx, tete, R * 1.15)
  grd.addColorStop(0, rgba('#0b0814', 0))
  grd.addColorStop(1, rgba('#0b0814', .9 - revele * .5))
  g.fillStyle = grd; g.fillRect(0, 0, W, H)
  g.restore()

  /* le faisceau : un cône additif qui s'ouvre et BALAIE légèrement. Sans ce
     balayage, la fin de l'animation est figée. */
  if (lampe > 0) {
    const ouvre = doux(lampe)
    g.save(); g.globalCompositeOperation = 'lighter'
    g.translate(cx + 6, tete - 4); g.rotate(.12 + Math.sin(ms / 620) * .1)
    const L = 300 * ouvre
    const cone = g.createLinearGradient(0, 0, L, 0)
    cone.addColorStop(0, rgba(OR_CLAIR, .3 * ouvre))
    cone.addColorStop(1, rgba(OR, 0))
    g.fillStyle = cone
    g.beginPath(); g.moveTo(0, 0); g.lineTo(L, -L * .34); g.lineTo(L, L * .34); g.closePath(); g.fill()
    g.restore()
  }

  /* le héros est peint APRÈS le voile : sans ça les deux premières secondes ne
     montrent rien du tout */
  heros(g, s, cx, ySol, POSES.idle!, ms / 16)

  if (allumette > 0 && allumette < 1) {
    const vif = Math.sin(ms / 40) * .18 + 1
    const fy = tete + 12 - allumette * 8
    g.save(); g.globalCompositeOperation = 'lighter'
    g.fillStyle = rgba('#ffb03a', .5 * (1 - allumette * .3))
    g.beginPath(); g.arc(cx + 10, fy, 26 * vif * (1 - allumette * .4), 0, 7); g.fill()
    g.fillStyle = rgba(OR_CLAIR, .9)
    g.beginPath(); g.ellipse(cx + 10, fy, 3 * vif, 6 * vif, 0, 0, 7); g.fill()
    g.restore()
  }
  const clac = ph(ms, 1050, 1180)
  if (clac > 0 && clac < 1) {
    g.save(); g.globalCompositeOperation = 'lighter'
    g.fillStyle = rgba('#ffffff', .5 * (1 - clac))
    g.beginPath(); g.arc(cx + 6, tete - 4, 40 * (1 - clac) + 8, 0, 7); g.fill()
    g.restore()
  }
}

/* ═══════════════════════════════════════════════════════════════════ L'AIGUILLAGE */

const PEINTRES: Record<string, (g: Ctx, W: number, H: number, ms: number, s: SceneEntree) => void> = {
  'entree-cage': entreeCage,
  'entree-percement': entreePercement,
  'entree-reception': entreeReception,
  'entree-wagonnet': entreeWagonnet,
  'entree-rideau': entreeRideau,
  'entree-lampe': entreeLampe,
}

/** Vrai si cet identifiant a un dessin. Un identifiant inconnu ne doit jamais
 *  faire jouer une animation vide de trois secondes : l'appelant saute l'entrée. */
export function entreeDessinee(id: string): boolean {
  return id in PEINTRES
}

/**
 * Peint l'entrée `id` à l'instant `ms` (0 à ENTREE_MS), par-dessus le niveau
 * déjà rendu. Ne touche à rien d'autre : ni état, ni chrono, ni son.
 */
export function peintEntree(g: Ctx, W: number, H: number, ms: number, id: string, s: SceneEntree): void {
  const f = PEINTRES[id]
  if (!f) return
  g.save()
  f(g, W, H, Math.max(0, Math.min(ENTREE_MS, ms)), s)
  g.restore()
}
